import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Pagination,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { api } from '../../api/client';
import {
  downloadExcelBuffer,
  formatDateForExport,
  styleExcelHeaderRow,
  todayFileStamp,
  writeLandscapePdfTable,
} from '../../utils/adminReportExport';

interface Payment {
  id: number;
  paymentReference: string;
  paynowReference?: string;
  amount: number;
  currency: string;
  paymentStatus: string;
  paymentType: string;
  payerEmail?: string;
  payerPhone?: string;
  description?: string;
  userId?: string;
  clientLoanId?: number;
  trackingNumber?: number;
  initiatedAt: string;
  completedAt?: string;
  failedAt?: string;
  failureReason?: string;
}

interface PaymentStatistics {
  totalPayments: number;
  totalSuccessfulAmount: number;
  successfulPayments: number;
  pendingPayments: number;
  failedPayments: number;
  successRate: number;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
}

interface PaymentsResponse {
  content: Payment[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

const PaymentManagement: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [statistics, setStatistics] = useState<PaymentStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  
  // Filters
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadPayments();
    loadStatistics();
  }, [page, pageSize]);

  const loadPayments = async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {
        page,
        size: pageSize,
      };
      
      if (userId) params.userId = userId;
      if (status) params.status = status;
      if (paymentType) params.paymentType = paymentType;
      if (payerEmail) params.payerEmail = payerEmail;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key].toString());
        }
      });
      
      const url = `/api/admin/payments${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await api.get<PaymentsResponse>(url);
      setPayments(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await api.get<PaymentStatistics>('/api/admin/payments/statistics');
      setStatistics(stats.data);
    } catch (err: any) {
      console.error('Failed to load payment statistics:', err);
    }
  };

  const handleSearch = () => {
    setPage(0);
    loadPayments();
  };

  const handleClearFilters = () => {
    setUserId('');
    setStatus('');
    setPaymentType('');
    setPayerEmail('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status.toUpperCase()) {
      case 'PAID':
      case 'DELIVERED':
        return 'success';
      case 'PENDING':
      case 'AWAITING_DELIVERY':
        return 'warning';
      case 'FAILED':
      case 'CANCELLED':
      case 'DISPUTED':
      case 'REFUNDED':
        return 'error';
      default:
        return 'info';
    }
  };

  const detectFraudIndicators = (payment: Payment): string[] => {
    const indicators: string[] = [];
    
    // Check for unusual amounts
    if (payment.amount > 100000) {
      indicators.push('Unusually high amount');
    }
    
    // Check for rapid repeated payments
    // This would require checking other payments, but for now we'll flag based on amount patterns
    
    // Check for missing critical information
    if (!payment.payerEmail && !payment.payerPhone) {
      indicators.push('Missing payer contact information');
    }
    
    // Check for failed payments with same reference
    if (payment.paymentStatus === 'FAILED' && payment.failureReason) {
      indicators.push(`Failed: ${payment.failureReason}`);
    }
    
    return indicators;
  };

  const exportToPDF = () => {
    const subtitleParts = [];
    if (startDate || endDate) {
      subtitleParts.push(`Date Range: ${startDate || 'All'} to ${endDate || 'All'}`);
    }
    if (status) subtitleParts.push(`Status: ${status}`);
    if (paymentType) subtitleParts.push(`Type: ${paymentType}`);

    writeLandscapePdfTable({
      title: 'Payments Report',
      subtitle: subtitleParts.length ? subtitleParts.join(' | ') : undefined,
      headers: ['Reference', 'Amount', 'Status', 'Type', 'Payer', 'Initiated', 'Completed'],
      colWidths: [40, 30, 28, 30, 45, 40, 40],
      rows: payments.map((p) => [
        p.paymentReference || 'N/A',
        formatCurrency(p.amount, p.currency),
        p.paymentStatus || 'N/A',
        p.paymentType || 'N/A',
        p.payerEmail || p.payerPhone || 'N/A',
        formatDateForExport(p.initiatedAt),
        formatDateForExport(p.completedAt),
      ]),
      fileName: `payments-report-${todayFileStamp()}.pdf`,
    });
  };

  const exportToExcel = async () => {
    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Payments');

      worksheet.columns = [
        { header: 'ID', key: 'id', width: 10 },
        { header: 'Payment Reference', key: 'paymentReference', width: 28 },
        { header: 'Paynow Reference', key: 'paynowReference', width: 22 },
        { header: 'Amount', key: 'amount', width: 14 },
        { header: 'Currency', key: 'currency', width: 10 },
        { header: 'Status', key: 'paymentStatus', width: 16 },
        { header: 'Type', key: 'paymentType', width: 18 },
        { header: 'Payer Email', key: 'payerEmail', width: 28 },
        { header: 'Payer Phone', key: 'payerPhone', width: 16 },
        { header: 'User ID', key: 'userId', width: 28 },
        { header: 'Loan ID', key: 'clientLoanId', width: 12 },
        { header: 'Tracking #', key: 'trackingNumber', width: 14 },
        { header: 'Initiated At', key: 'initiatedAt', width: 20 },
        { header: 'Completed At', key: 'completedAt', width: 20 },
        { header: 'Failed At', key: 'failedAt', width: 20 },
        { header: 'Failure Reason', key: 'failureReason', width: 40 },
        { header: 'Description', key: 'description', width: 40 },
        { header: 'Fraud Indicators', key: 'fraudIndicators', width: 40 },
      ];
      styleExcelHeaderRow(worksheet);

      payments.forEach((p) => {
        worksheet.addRow({
          id: p.id,
          paymentReference: p.paymentReference || 'N/A',
          paynowReference: p.paynowReference || '',
          amount: p.amount,
          currency: p.currency || 'USD',
          paymentStatus: p.paymentStatus || 'N/A',
          paymentType: p.paymentType || 'N/A',
          payerEmail: p.payerEmail || '',
          payerPhone: p.payerPhone || '',
          userId: p.userId || '',
          clientLoanId: p.clientLoanId ?? '',
          trackingNumber: p.trackingNumber ?? '',
          initiatedAt: formatDateForExport(p.initiatedAt),
          completedAt: formatDateForExport(p.completedAt),
          failedAt: formatDateForExport(p.failedAt),
          failureReason: p.failureReason || '',
          description: p.description || '',
          fraudIndicators: detectFraudIndicators(p).join('; ') || '',
        });
      });

      const metadataSheet = workbook.addWorksheet('Export Info');
      metadataSheet.columns = [
        { header: 'Property', key: 'property', width: 25 },
        { header: 'Value', key: 'value', width: 50 },
      ];
      styleExcelHeaderRow(metadataSheet);
      metadataSheet.addRows([
        { property: 'Export Date', value: new Date().toLocaleString() },
        { property: 'Total Records (filtered set)', value: totalElements },
        { property: 'Exported Page Records', value: payments.length },
        { property: 'User ID Filter', value: userId || 'All' },
        { property: 'Status Filter', value: status || 'All' },
        { property: 'Payment Type Filter', value: paymentType || 'All' },
        { property: 'Payer Email Filter', value: payerEmail || 'All' },
        { property: 'Start Date', value: startDate || 'All' },
        { property: 'End Date', value: endDate || 'All' },
        {
          property: 'Success Rate',
          value: statistics ? `${statistics.successRate.toFixed(1)}%` : 'N/A',
        },
        {
          property: 'Total Successful Amount',
          value: statistics ? statistics.totalSuccessfulAmount : 'N/A',
        },
      ]);

      const buffer = await workbook.xlsx.writeBuffer();
      await downloadExcelBuffer(buffer, `payments-report-${todayFileStamp()}.xlsx`);
    } catch (err) {
      console.error('Failed to export Excel:', err);
      alert('Failed to export Excel. Please ensure exceljs is installed.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Payment Management & Fraud Analysis
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportToPDF}
            disabled={loading || payments.length === 0}
            color="error"
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableChartIcon />}
            onClick={exportToExcel}
            disabled={loading || payments.length === 0}
            color="success"
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Payments
                </Typography>
                <Typography variant="h4">{statistics.totalPayments.toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Amount Processed
                </Typography>
                <Typography variant="h4" color="success.main">
                  {formatCurrency(statistics.totalSuccessfulAmount, 'USD')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Success Rate
                </Typography>
                <Typography variant="h4" color="success.main">
                  {statistics.successRate.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Pending Payments
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {statistics.pendingPayments.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
                <MenuItem value="DELIVERED">Delivered</MenuItem>
                <MenuItem value="AWAITING_DELIVERY">Awaiting Delivery</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                <MenuItem value="DISPUTED">Disputed</MenuItem>
                <MenuItem value="REFUNDED">Refunded</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Type</InputLabel>
              <Select value={paymentType} label="Payment Type" onChange={(e) => setPaymentType(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="LOAN_FEE">Loan Fee</MenuItem>
                <MenuItem value="LOAN_REPAYMENT">Loan Repayment</MenuItem>
                <MenuItem value="PROCESSING_FEE">Processing Fee</MenuItem>
                <MenuItem value="INSURANCE_FEE">Insurance Fee</MenuItem>
                <MenuItem value="PENALTY_FEE">Penalty Fee</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Payer Email"
              value={payerEmail}
              onChange={(e) => setPayerEmail(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Start Date"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="End Date"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
                Search
              </Button>
              <Tooltip title="Refresh">
                <IconButton onClick={loadPayments} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              {(userId || status || paymentType || payerEmail || startDate || endDate) && (
                <Button size="small" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Payments Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : payments.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No payments found</Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reference</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payer</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Fraud Indicators</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => {
                  const fraudIndicators = detectFraudIndicators(payment);
                  return (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {payment.paymentReference}
                        </Typography>
                        {payment.paynowReference && (
                          <Typography variant="caption" color="text.secondary">
                            Paynow: {payment.paynowReference}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(payment.amount, payment.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={payment.paymentType.replace('_', ' ')} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.paymentStatus}
                          size="small"
                          color={getStatusColor(payment.paymentStatus)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{payment.payerEmail || 'N/A'}</Typography>
                        {payment.payerPhone && (
                          <Typography variant="caption" color="text.secondary">
                            {payment.payerPhone}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(payment.initiatedAt)}</TableCell>
                      <TableCell>
                        {fraudIndicators.length > 0 ? (
                          <Tooltip title={fraudIndicators.join(', ')}>
                            <Chip
                              icon={<WarningIcon />}
                              label={`${fraudIndicators.length} Alert${fraudIndicators.length > 1 ? 's' : ''}`}
                              size="small"
                              color="warning"
                            />
                          </Tooltip>
                        ) : (
                          <Chip icon={<CheckCircleIcon />} label="OK" size="small" color="success" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setDetailDialogOpen(true);
                          }}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {payments.length} of {totalElements} payments
              </Typography>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Page Size</InputLabel>
                <Select
                  value={pageSize}
                  label="Page Size"
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                  }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}
      </TableContainer>

      {/* Payment Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Payment Details</DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Payment Reference</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {selectedPayment.paymentReference}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedPayment.paymentStatus}
                  color={getStatusColor(selectedPayment.paymentStatus)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Payment Type</Typography>
                <Typography variant="body1">{selectedPayment.paymentType.replace('_', ' ')}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Payer Email</Typography>
                <Typography variant="body1">{selectedPayment.payerEmail || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Payer Phone</Typography>
                <Typography variant="body1">{selectedPayment.payerPhone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Initiated At</Typography>
                <Typography variant="body1">{formatDate(selectedPayment.initiatedAt)}</Typography>
              </Grid>
              {selectedPayment.completedAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Completed At</Typography>
                  <Typography variant="body1">{formatDate(selectedPayment.completedAt)}</Typography>
                </Grid>
              )}
              {selectedPayment.failureReason && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Failure Reason</Typography>
                  <Typography variant="body1" color="error">
                    {selectedPayment.failureReason}
                  </Typography>
                </Grid>
              )}
              {selectedPayment.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{selectedPayment.description}</Typography>
                </Grid>
              )}
              {detectFraudIndicators(selectedPayment).length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning" icon={<WarningIcon />}>
                    <Typography variant="subtitle2" fontWeight="bold">Fraud Indicators:</Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {detectFraudIndicators(selectedPayment).map((indicator, idx) => (
                        <li key={idx}>{indicator}</li>
                      ))}
                    </ul>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentManagement;

